import { renderRowsWithSpacing } from "@/lib/accordion-content";
import type { TirageHistory } from "@/api/tirages/tirage-history.api";
import type { Auction } from "@/lib/types";

/** Merged API timeline row (includes `kind`). */
export type TirageHistoryMergedAuction = {
  kind: "auction";
  id: string;
  tirageId: string | null;
  provenanceRank?: number;
  tirageDansVentesDescriptionNumeroLot?: string;
  auction: {
    ventesDescriptionCommissaires?: string;
    ventesDescriptionLieu?: string;
    ventesDescriptionVille?: string;
    ventesDescriptionPays?: string;
    ventesDescriptionDate?: string;
  } | null;
};

/** Minimal tirage-in-auction shape for provenance rendering (TirageInAuction satisfies this). */
export type TirageProvenanceAuctionInput = {
  id?: string;
  provenanceRank?: number;
  tirageDansVentesDescriptionNumeroLot?: string;
  oeuvresDansVentesDescriptionNumeroLot?: string;
  auction?: Auction | null;
};

type ProvenanceRow =
  | { kind: "history"; index: number; item: TirageHistory }
  | { kind: "auction"; index: number; item: TirageProvenanceAuctionInput };

const cleanParts = (parts: Array<string | null | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean) as string[];

const hasPersistedOrder = (rows: ProvenanceRow[]) =>
  rows.some((row) => (row.item.provenanceRank ?? 0) > 0);

const getFallbackOrder = (row: ProvenanceRow, historyCount: number) => {
  if (row.kind === "history") return row.item.id_imported ?? row.index;
  return historyCount + row.index;
};

export function buildTirageProvenance({
  history,
  auctions,
  t,
}: {
  history?: TirageHistory[];
  auctions?: TirageProvenanceAuctionInput[];
  t: (key: string) => string;
}) {
  const historyRows: ProvenanceRow[] = (history ?? []).map((item, index) => ({
    kind: "history",
    item,
    index,
  }));
  const auctionRows: ProvenanceRow[] = (auctions ?? []).map((item, index) => ({
    kind: "auction",
    item,
    index,
  }));
  const rows = [...historyRows, ...auctionRows];
  if (rows.length === 0) return null;

  const shouldUsePersistedOrder = hasPersistedOrder(rows);
  rows.sort((a, b) => {
    const aRank = shouldUsePersistedOrder
      ? (a.item.provenanceRank ?? 0)
      : getFallbackOrder(a, historyRows.length);
    const bRank = shouldUsePersistedOrder
      ? (b.item.provenanceRank ?? 0)
      : getFallbackOrder(b, historyRows.length);
    if (aRank !== bRank) return aRank - bRank;
    if (a.kind !== b.kind) return a.kind === "history" ? -1 : 1;
    return (a.item.id ?? "").localeCompare(b.item.id ?? "");
  });

  const renderedRows = rows
    .map((row) => {
      if (row.kind === "history") {
        const item = row.item;
        const location = item.tirageHistoriqueHistoriqueCollectionParticuliere
          ? t("computeField.collectionParticuliere")
          : item.tirageHistoriqueHistoriqueNom;
        return cleanParts([
          location,
          item.tirageHistoriqueHistoriqueVille,
          item.tirageHistoriqueHistoriqueDates,
        ]).join(", ");
      }

      const item = row.item;
      const auction = item.auction;
      const location = cleanParts([
        auction?.ventesDescriptionCommissaires,
        auction?.ventesDescriptionLieu,
        auction?.ventesDescriptionVille,
        auction?.ventesDescriptionPays,
        (item.tirageDansVentesDescriptionNumeroLot ??
        (item as { oeuvresDansVentesDescriptionNumeroLot?: string })
          .oeuvresDansVentesDescriptionNumeroLot)
          ? `lot n°${item.tirageDansVentesDescriptionNumeroLot ?? (item as { oeuvresDansVentesDescriptionNumeroLot?: string }).oeuvresDansVentesDescriptionNumeroLot}`
          : null,
      ]).join(", ");
      return cleanParts([auction?.ventesDescriptionDate, location]).join(" - ");
    })
    .filter(Boolean);

  return renderedRows.length > 0 ? renderRowsWithSpacing(renderedRows) : null;
}
